import { useCallback, useState } from 'react';

import { PopoverTrigger } from '@radix-ui/react-popover';
import {
  ColumnFiltersState,
  createColumnHelper,
  FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  filterFns,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { Inconsistency } from '../../../../../../../../../../backend/src/matter/inconsistency/inconsistency.entity';
import { DownloadFormat, Relevance } from '../../../../../../../../../../backend/src/types/types';
import {
  inconsistencyReportDownloadColumnOptions,
  useDownloadInconsistencyReport,
} from '@/api/mutations/useDownloadInconsistencyReport';
import { useGetAllInconsistencyReportItems } from '@/api/queries/useGetAllInconsistencyReportItems';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { DownloadTableModal } from '@/new/components/dialogs/download-dialog/download-table';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { FilterPopover } from '@/new/components/inconsistency/filter-popover';
import { InconsistencyCard } from '@/new/components/inconsistency/inconsistency-card';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Accordion } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Popover } from '@/new/components/shadcn/popover';
import { TablePagination } from '@/new/components/table/table-pagination';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { includedInArr } from '@/new/lib/table-utils';
import { buildClientSideFilterPills } from '@/utils/buildFilterPills';

const fallbackTableData: Inconsistency[] = [];
const columnHelper = createColumnHelper<Inconsistency>();

const columns = [
  columnHelper.accessor('level', { filterFn: includedInArr }),
  columnHelper.accessor('description', {}),
  columnHelper.accessor('type', { filterFn: includedInArr }),
  columnHelper.accessor('source', { filterFn: includedInArr }),
];

const customGlobalFilter: FilterFn<Inconsistency> = (
  row,
  columnId,
  filterValue: { query: string; hasRelevantFactsFilter: boolean },
) => {
  const { query, hasRelevantFactsFilter } = filterValue;

  if (hasRelevantFactsFilter && !row.original.facts.some((f) => f.fact.relevance === Relevance.Relevant)) {
    return false;
  }

  if (query) {
    return filterFns.includesString(row, columnId, query, () => null);
  }

  return true;
};

export function Report() {
  const { matterId, subsetId, reportId } = useParams();
  if (!matterId || !subsetId || !reportId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { data: report } = useGetAllInconsistencyReportItems({ caseId: subsetId, reportId });

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Inconsistency Reports', path: 'reports' },
  });

  const [globalFilter, setGlobalFilter] = useState({ query: '', hasRelevantFactsFilter: false });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);

  const { fetchDownload, isLoadingDownload } = useDownloadInconsistencyReport({
    reportId,
    caseId: subsetId,
  });

  const clearAllFilters = () => {
    setGlobalFilter({ query: '', hasRelevantFactsFilter: false });
    setColumnFilters([]);
  };

  const handleDownload = useCallback(
    ({ title, columns, downloadFormat }: { title: string; columns: string[]; downloadFormat: DownloadFormat }) => {
      fetchDownload(
        { title, columns, format: downloadFormat },
        {
          onSuccess: async () => {
            await new Promise((resolve) => setTimeout(resolve, 200));
            setDownloadModalIsOpen(false);
          },
        },
      );
    },
    [fetchDownload],
  );

  const table = useReactTable({
    data: report?.inconsistencies ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    globalFilterFn: customGlobalFilter,
    state: {
      globalFilter,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
      sorting: [{ id: 'level', desc: false }],
    },
    onColumnFiltersChange: setColumnFilters,
    enableRowSelection: false,
  });

  return (
    <>
      <Breadcrumb
        items={[
          ...breadcrumbItems.map((item, index) =>
            index === breadcrumbItems.length - 1 ? { ...item, active: false } : item,
          ),
          {
            href: `/matters/${matterId}/subsets/${subsetId}/reports/${reportId}`,
            label: report?.title ?? 'Report',
            active: true,
          },
        ]}
      />

      <div className="flex h-full flex-col overflow-hidden">
        <PageContainer>
          <PageHeader>
            <PageTitle text={`Report: ${report?.title ?? ''}`} />
          </PageHeader>

          <div className="flex w-full gap-2">
            <SearchInput
              placeholder="Search inconsistencies..."
              className="grow"
              value={globalFilter.query}
              onChange={(e) => setGlobalFilter({ ...globalFilter, query: e.target.value })}
            />

            <Dialog open={downloadModalIsOpen} onOpenChange={setDownloadModalIsOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Icon className="lucide--download" />
                  <span>Download Report</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DownloadTableModal
                  columnOptions={inconsistencyReportDownloadColumnOptions.map((o) => o.value)}
                  downloadTitle={report?.title ?? ''}
                  loadingDownload={isLoadingDownload}
                  handleDownload={handleDownload}
                />
              </DialogContent>
            </Dialog>

            <Popover>
              <PopoverTrigger asChild>
                <Button disabled={!report}>
                  <Icon className="lucide--funnel-plus" />
                  <span>Add Filters</span>
                </Button>
              </PopoverTrigger>
              {report && (
                <FilterPopover
                  inconsistencies={report.inconsistencies}
                  typeColumn={table.getColumn('type')}
                  levelColumn={table.getColumn('level')}
                  sourceColumn={table.getColumn('source')}
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  clearAllFilters={clearAllFilters}
                />
              )}
            </Popover>

            <Button onClick={clearAllFilters}>
              <Icon className="lucide--funnel-x" />
              <span>Clear All Filters</span>
            </Button>
          </div>

          <FilterPills pills={buildClientSideFilterPills(columnFilters, setColumnFilters)} />

          <div className="flex items-center justify-end pt-4 pb-3">
            <TablePagination table={table} plural="inconsistencies" variant="sm" />
          </div>
        </PageContainer>

        <Accordion type="multiple" className="mx-5 flex shrink flex-col gap-5 overflow-auto">
          {table.getRowModel().rows.map((row) => (
            <InconsistencyCard key={row.id} row={row} inconsistency={row.original} />
          ))}
        </Accordion>
      </div>
    </>
  );
}
